import { useEffect, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Textfield } from "@similarweb/ui-components";

import { useProjectCreation } from "app/modules/project-from-comparison/context";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { useExistingProjects } from "app/modules/project-from-comparison/helpers/useExistingProjects";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { ProjectErrors } from "app/modules/project-from-comparison/types";
import useNameInput from "app/pages/activation/hooks/setup/useNameInput";

import * as SC from "./styles";
import { PROJECT_NAME_LABEL, PROJECT_NAME_PREFIX } from "./text";

export const NameInput = () => {
    const { setProjectName, domains, setErrors } = useProjectCreation();
    const { existingNames } = useExistingProjects();
    const { onNameChange, nameError, activationSetName } = useNameInput(existingNames);
    const textFieldRef = useRef<Textfield>(null);
    const { translate } = useTranslate();
    const primaryDomain = useRef("");

    const handleProjectNameChange = (value: string) => {
        setProjectName(value);
        onNameChange(value);
    };

    useEffect(() => {
        if (textFieldRef.current?.getValue() === activationSetName) {
            return;
        }

        textFieldRef.current?.setValue(activationSetName);
    }, [activationSetName]);

    useEffect(() => {
        if (primaryDomain.current === domains[0]) {
            return;
        }

        const name = `${translate(PROJECT_NAME_PREFIX)} ${domains[0]}`;

        primaryDomain.current = domains[0];
        onNameChange(name);
        setProjectName(name);
    }, [domains, onNameChange, setProjectName, translate]);

    useEffect(() => {
        setErrors((prevState) => ({ ...prevState, [ProjectErrors.Name]: nameError.errorMessage }));

        if (nameError.errorMessage.includes(activationSetName) && nameError.isError) {
            projectCreatingTrack.trackProjectNameAlreadyExistsError();

            return;
        }
    }, [activationSetName, nameError, setErrors]);

    return (
        <SC.Container data-automation={projectCreationDataAutomation.nameInput.container}>
            <Textfield
                ref={textFieldRef}
                label={translate(PROJECT_NAME_LABEL)}
                onChange={handleProjectNameChange}
                errorMessage={nameError.errorMessage}
                error={nameError.isError}
                dataAutomation={projectCreationDataAutomation.nameInput.textfield}
            />
        </SC.Container>
    );
};
